import {
  ON_MARS_SOURCE_LABEL,
  OnMarsSectionSource,
} from './navigationTypes';
import { onMarsSectionRegistry, getOnMarsSection } from './sectionRegistry';
import {
  ON_MARS_CORE_SEARCH_KEYWORDS,
  ON_MARS_SECTION_KEYWORD_BOOST,
} from './searchKeywords';
import {
  buildOnMarsSearchDocuments,
  normalizeSearchQuery,
  snippetFromBody,
  type OnMarsSearchDocument,
} from './searchDocuments';
import { onMarsManual, onMarsQuickReference } from '../onMars';

export type OnMarsSearchHit = {
  id: string;
  kind: OnMarsSectionSource;
  kindLabel: string;
  title: string;
  snippet: string;
  sectionId: string;
  tags: string[];
};

let cachedDocuments: OnMarsSearchDocument[] | null = null;

function getDocuments(): OnMarsSearchDocument[] {
  if (!cachedDocuments) {
    cachedDocuments = buildOnMarsSearchDocuments({
      manual: onMarsManual,
      quickReference: onMarsQuickReference,
    });
  }
  return cachedDocuments;
}

/** Full-body search across chapters, FAQ, QR, mistakes, glossary, guided, scoring. */
export function searchOnMarsModule(query: string): OnMarsSearchHit[] {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) {
    return [];
  }

  const seen = new Set<string>();
  const hits: OnMarsSearchHit[] = [];

  for (const doc of getDocuments()) {
    if (seen.has(doc.sectionId)) continue;

    const boost = ON_MARS_SECTION_KEYWORD_BOOST[doc.sectionId] ?? [];
    const registry = getOnMarsSection(doc.sectionId);
    const haystack = normalizeSearchQuery(
      [
        doc.title,
        doc.body,
        doc.kindLabel,
        ...(registry?.keywords ?? []),
        ...boost,
        ...ON_MARS_CORE_SEARCH_KEYWORDS,
      ].join(' ')
    );

    if (!haystack.includes(normalized)) continue;

    seen.add(doc.sectionId);
    hits.push({
      id: doc.sectionId,
      kind: doc.kind,
      kindLabel: doc.kindLabel,
      title: doc.title,
      snippet: snippetFromBody(doc.body, normalized),
      sectionId: doc.sectionId,
      tags: [...(registry?.keywords ?? []), ...boost],
    });
  }

  return hits;
}

/** @deprecated kept for import compatibility — prefer searchOnMarsModule */
export const onMarsSearchIndex = onMarsSectionRegistry.map((section) => ({
  id: section.id,
  kind: section.source,
  title: section.title,
  snippet: ON_MARS_SOURCE_LABEL[section.source],
  tags: section.keywords ?? [],
}));
